import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Shield, PencilSimple, Trash, Plus, LockKey, Info } from "@phosphor-icons/react";

const LEVEL_LABEL = {
  none: "No Access",
  view: "View Only",
  limited: "Limited",
  full: "Full Access",
};

const LEVEL_TONE = {
  none: "bg-slate-100 text-slate-500",
  view: "bg-blue-100 text-blue-800",
  limited: "bg-amber-100 text-amber-800",
  full: "bg-emerald-100 text-emerald-800",
};

const MODULE_LABEL = {
  dashboard: "Dashboard",
  attendance: "Attendance",
  student_queue: "Student Queue",
  therapist_queue: "Therapist Queue",
  sessions: "Sessions",
  student: "Student",
  message: "Message",
  invoice: "Invoice",
  grants: "Grants",
  inventory: "Inventory",
  certifications: "Certifications",
  reports: "Reports",
  logs: "Logs",
  settings: "Settings",
  appointments: "Appointments",
  my_approvals: "My Approvals",
  publish: "Publish",
};

const emptyPermissions = (modules) =>
  modules.reduce((acc, m) => { acc[m] = "none"; return acc; }, {});

export default function RolesTab() {
  const { activeRole } = useAuth();
  const canManage = ["owner", "admin"].includes(activeRole);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { key?, name, description, permissions, builtin }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/modules"),
      ]);
      setRoles(r.data.roles || []);
      setModules(m.data.modules || []);
      setHints(m.data.limited_hints || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing({
      key: "", name: "", description: "",
      permissions: emptyPermissions(modules),
      builtin: false,
      isNew: true,
    });
  };

  const openEdit = (r) => {
    setEditing({
      key: r.key,
      name: r.name,
      description: r.description || "",
      permissions: { ...emptyPermissions(modules), ...(r.permissions || {}) },
      builtin: !!r.builtin,
      isNew: false,
    });
  };

  const save = async () => {
    if (!editing) return;
    if (editing.isNew) {
      if (!/^[A-Za-z0-9_]{2,32}$/.test(editing.key || "")) {
        toast.error("Key must be 2–32 characters, letters, digits or underscore only");
        return;
      }
    }
    setSaving(true);
    try {
      if (editing.isNew) {
        await api.post("/roles", {
          key: editing.key,
          name: editing.name || editing.key,
          description: editing.description || null,
          permissions: editing.permissions,
        });
        toast.success(`Role '${editing.key}' created`);
      } else {
        await api.put(`/roles/${editing.key}`, {
          ...(editing.builtin ? {} : { name: editing.name }),
          description: editing.description || null,
          permissions: editing.permissions,
        });
        toast.success(`Role '${editing.key}' updated`);
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    if (r.builtin) { toast.error("Built-in roles can't be deleted"); return; }
    if (!window.confirm(
      `Delete the '${r.key}' role?\n\nUsers currently assigned this role will need to be reassigned first — the delete will fail otherwise.`
    )) return;
    try {
      await api.delete(`/roles/${r.key}`);
      toast.success(`Role '${r.key}' deleted`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const builtinRoles = useMemo(() => roles.filter(r => r.builtin), [roles]);
  const customRoles = useMemo(() => roles.filter(r => !r.builtin), [roles]);

  return (
    <div className="space-y-6" data-testid="roles-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg tracking-tight flex items-center gap-2">
            <Shield size={18} weight="duotone" /> Roles & Permissions
          </h3>
          <p className="text-xs text-muted-foreground max-w-2xl mt-0.5">
            Built-in roles ship with sensible defaults — you can tweak permissions on any of them,
            but their names stay locked. Create custom roles for anything else your workspace needs.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="gap-1.5" data-testid="create-role-btn">
            <Plus size={14} weight="bold" /> Create custom role
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          <RoleGrid
            title="Built-in roles" subtitle="Ship with the platform. Cannot be renamed or deleted."
            roles={builtinRoles} onEdit={openEdit} onDelete={null} canManage={canManage}
            modules={modules}
          />
          <RoleGrid
            title="Custom roles" subtitle="Pre-built examples + anything you create."
            roles={customRoles} onEdit={openEdit} onDelete={del} canManage={canManage}
            modules={modules}
          />
        </>
      )}

      {editing && (
        <RoleEditorDialog
          editing={editing} setEditing={setEditing}
          modules={modules} hints={hints}
          saving={saving} onSave={save} onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RoleGrid({ title, subtitle, roles, onEdit, onDelete, canManage, modules }) {
  if (!roles.length) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          {title}
          <span className="text-xs font-normal text-muted-foreground">· {roles.length}</span>
        </CardTitle>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {roles.map(r => {
            const counts = modules.reduce((acc, m) => {
              const v = (r.permissions || {})[m] || "none";
              acc[v] = (acc[v] || 0) + 1;
              return acc;
            }, {});
            return (
              <div key={r.key} className="p-4 flex items-start gap-4" data-testid={`role-row-${r.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.name}</span>
                    <code className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{r.key}</code>
                    {r.builtin && <Badge variant="outline" className="text-[10px] gap-1"><LockKey size={10} /> built-in</Badge>}
                    {r.no_login && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">No login</Badge>}
                  </div>
                  {r.description && <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>}
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {["full","limited","view","none"].map(lvl => counts[lvl] ? (
                      <span key={lvl} className={`text-[10px] px-1.5 py-0.5 rounded ${LEVEL_TONE[lvl]}`}>
                        {LEVEL_LABEL[lvl]}: {counts[lvl]}
                      </span>
                    ) : null)}
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => onEdit(r)}
                      className="h-8 gap-1 text-xs" data-testid={`edit-role-${r.key}`}>
                      <PencilSimple size={12} /> Edit
                    </Button>
                    {onDelete && (
                      <Button size="sm" variant="ghost" onClick={() => onDelete(r)}
                        className="h-8 gap-1 text-xs text-red-700 hover:bg-red-50" data-testid={`delete-role-${r.key}`}>
                        <Trash size={12} /> Delete
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function RoleEditorDialog({ editing, setEditing, modules, hints, saving, onSave, onCancel }) {
  const setPerm = (mod, lvl) => setEditing(e => ({ ...e, permissions: { ...e.permissions, [mod]: lvl } }));
  return (
    <Dialog open={!!editing} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            {editing.isNew ? "Create custom role" : `Edit '${editing.name}'`}
            {editing.builtin && <Badge variant="outline" className="text-[10px] gap-1"><LockKey size={10} /> built-in</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Role key {editing.isNew && <span className="text-muted-foreground text-[11px]">(letters, digits, underscore)</span>}</Label>
            <Input
              value={editing.key}
              readOnly={!editing.isNew}
              disabled={!editing.isNew}
              onChange={e => setEditing(v => ({ ...v, key: e.target.value }))}
              placeholder="e.g. Front_Desk_Manager"
              data-testid="role-editor-key"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Display name {editing.builtin && <span className="text-muted-foreground text-[11px]">(locked for built-ins)</span>}</Label>
            <Input
              value={editing.name}
              readOnly={editing.builtin}
              disabled={editing.builtin}
              onChange={e => setEditing(v => ({ ...v, name: e.target.value }))}
              placeholder="Front Desk Manager"
              data-testid="role-editor-name"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Description</Label>
          <Textarea
            rows={2}
            value={editing.description}
            onChange={e => setEditing(v => ({ ...v, description: e.target.value }))}
            placeholder="What this role covers"
          />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold flex items-center gap-1.5">
            Module permissions
            <Info size={12} className="text-muted-foreground" />
          </div>
          <div className="border rounded-md divide-y divide-border">
            {modules.map(m => (
              <div key={m} className="p-3 grid grid-cols-[1fr_180px] items-center gap-3">
                <div>
                  <div className="text-sm font-medium">{MODULE_LABEL[m] || m}</div>
                  {editing.permissions[m] === "limited" && hints[m] && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 italic">{hints[m]}</div>
                  )}
                </div>
                <Select value={editing.permissions[m]} onValueChange={(v) => setPerm(m, v)}>
                  <SelectTrigger className="h-8" data-testid={`perm-${m}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="none">No Access</SelectItem>
                    <SelectItem value="view">View Only</SelectItem>
                    <SelectItem value="limited">Limited</SelectItem>
                    <SelectItem value="full">Full Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="role-editor-save">
            {saving ? "Saving…" : (editing.isNew ? "Create role" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
